import React from 'react'

import { Button, ButtonVariant } from '@amzn/stencil-react-components/button'
import { Input, InputWrapper } from '@amzn/stencil-react-components/form'
import { IconBin } from '@amzn/stencil-react-components/icons'
import { Row, Spacer, View } from '@amzn/stencil-react-components/layout'

import { ResponsiveRow } from 'src/components/ResponsiveRow'
import { ItemPoolDistributionDTO } from 'src/models/dto/activities/AdaptiveEngineSelectionGroupDTO'
import {
    ItemPoolModal,
    ItemPoolModalButtonProps,
} from 'src/pages/module-builder/SelfServeManualItemPoolBuilder/ItemPoolModal'

export interface AdaptiveEngineItemPoolDistributionEditorProps {
    itemPoolDistribution: ItemPoolDistributionDTO
    setItemPoolVersionId?: (value: string) => void
    setMinItems?: (value: number) => void
    setPercentage?: (value: number) => void
    removeItemPoolDistribution?: () => void
    isScored: boolean
    idPrefix?: string
}

export function AdaptiveEngineItemPoolDistributionEditor({
    itemPoolDistribution: { itemPoolId: itemPoolVersionId, minItems, percentage },
    setMinItems,
    setItemPoolVersionId,
    setPercentage,
    removeItemPoolDistribution,
    isScored,
    idPrefix = 'item-pool-distribution',
}: AdaptiveEngineItemPoolDistributionEditorProps) {
    return (
        <ResponsiveRow gridGap='S300' alignItems='flex-end'>
            <Row flex='3' alignItems='flex-end' gridGap='S200'>
                <View flex='1'>
                    <InputWrapper
                        id={`${idPrefix}-item-pool-version-id`}
                        labelText={`${isScored ? 'Scored' : 'Unscored'} Item Pool Version ID`}
                    >
                        {(inputProps) => (
                            <Input
                                {...inputProps}
                                type='text'
                                dataTestId={`${idPrefix}-item-pool-version-id`}
                                value={itemPoolVersionId}
                                onChange={(e) => setItemPoolVersionId?.(e.target.value)}
                            />
                        )}
                    </InputWrapper>
                </View>
                <View flex='0'>
                    <ItemPoolModal
                        itemPoolVersionId={itemPoolVersionId}
                        existingPool={false}
                        setSelectItemPoolVersionId={setItemPoolVersionId}
                    >
                        {({ onClick, disabled }: ItemPoolModalButtonProps) => (
                            <Button
                                dataTestId={`${idPrefix}-select-item-pool`}
                                style={{ width: '100%' }}
                                variant={ButtonVariant.Secondary}
                                disabled={disabled}
                                aria-disabled={disabled}
                                onClick={onClick}
                            >
                                Select item pool
                            </Button>
                        )}
                    </ItemPoolModal>
                </View>
            </Row>
            {!isScored && <Spacer flex='2 0 0' />}
            {isScored && (
                <>
                    <ResponsiveRow flex='1 1 0' gridGap='S300'>
                        <View flex='1 1 0' width='50%'>
                            <InputWrapper id={`${idPrefix}-min-items`} labelText='Minimum items'>
                                {(inputProps) => (
                                    <Input
                                        {...inputProps}
                                        type='number'
                                        dataTestId={`${idPrefix}-min-items`}
                                        min={0}
                                        step={1}
                                        value={minItems}
                                        onChange={(e) => {
                                            setMinItems?.(parseInt(e.target.value) || 0)
                                        }}
                                    />
                                )}
                            </InputWrapper>
                        </View>
                        <View flex='1 1 0' width='50%'>
                            <InputWrapper id={`${idPrefix}-pct`} labelText='Percentage'>
                                {(inputProps) => (
                                    <Input
                                        {...inputProps}
                                        flex='1'
                                        type='number'
                                        dataTestId={`${idPrefix}-pct`}
                                        min={0}
                                        max={100}
                                        value={percentage}
                                        onChange={(e) =>
                                            setPercentage?.(parseInt(e.target.value) || 0)
                                        }
                                    />
                                )}
                            </InputWrapper>
                        </View>
                    </ResponsiveRow>
                    <View flex='0 1 0'>
                        <Button
                            isDestructive={true}
                            dataTestId={`${idPrefix}-remove-item-pool`}
                            variant={ButtonVariant.Secondary}
                            onClick={removeItemPoolDistribution}
                            aria-label='Remove this item pool'
                            icon={<IconBin aria-hidden />}
                        />
                    </View>
                </>
            )}
        </ResponsiveRow>
    )
}
